import { GlobalContext } from "context/GlobalContext";
import { useContext, useEffect, useState } from "react";
import {
  CopyIcon,
  DeleteIcon,
  EditIcon,
  TableDatePicker,
  TopNewsIcon,
} from "themes/Icon";
import AvatarComp from "themes/elements/Avatar";
import EmptyState from "themes/elements/EmptyState";
import { isPermit } from "utils/permissionCheck";
import ButtonComp from "../../Button";
import DatePickerComp from "../../DatePicker";
import PaginationComp from "../../Pagination";
import SearchComp from "../../Search";
import SelectComp from "../../Select";
import TagComp from "../../Tag";
import TooltipComp from "../../Tooltip";
import "../table_comp.scss";

function NewsTable({
  column,
  data,
  dataClick,
  copyAction = true,
  onDeleteAction = () => {},
  currentPageNo,
  setCurrentPageNo = () => {},
  totalPageNo,
  handleEdit,
  handleDuplicate = () => {},
  selectedData,
  setSelectedData = () => {},
  isLoading,
  tabState,
  tabActive,
  filterTitle,
  filterSchedule,
  setFilterSchedule = () => {},
  setFilterExpiry = () => {},
  setFilterCategory = () => {},
  setFilterTitle = () => {},
  filterAuthor,
  setFilterAuthor = () => {},
  setFilterPublish = () => {},
  editAction = false,
}) {
  const [currentPageData, setCurrentPageData] = useState(data);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [alertDate, setAlertDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [scheduleDate, setScheduleDate] = useState(null);

  const { state } = useContext(GlobalContext);
  const { colorData } = state;

  useEffect(() => {
    setCurrentPageData(data);
    setSelectedData([]);
  }, [data]);

  const handlePageChange = (page) => {
    setCurrentPageNo(page);
  };

  useEffect(() => {
    clearFilters();
  }, [tabActive]);

  const formatDate = (dateString) => {
    const [day, month, year] = dateString.split("-");
    return `${year}-${month}-${day}`;
  };

  const handleAlertDateChange = (dateString) => {
    setAlertDate(dateString);
    setCurrentPageNo(1);
    if (dateString) {
      const formattedDate = formatDate(dateString);
      setFilterPublish(formattedDate);
    } else {
      setFilterPublish(null);
    }
  };

  const handleExpiryDateChange = (dateString) => {
    setCurrentPageNo(1);
    setExpiryDate(dateString);
    if (dateString) {
      const formattedDate = formatDate(dateString);
      setFilterExpiry(formattedDate);
    } else {
      setFilterExpiry(null);
    }
  };

  const handleScheduleDateChange = (dateString) => {
    setCurrentPageNo(1);
    setScheduleDate(dateString);
    if (dateString) {
      const formattedDate = formatDate(dateString);
      setFilterSchedule(formattedDate);
    } else {
      setFilterSchedule(null);
    }
  };

  const handleSearchChange = (event, type) => {
    const value = event.target.value;
    setCurrentPageNo(1);
    if (type === "title") {
      setFilterTitle(value);
    } else {
      setFilterAuthor(value);
    }

    if (value) {
      const lowercasedValue = value?.toLowerCase();
      const filteredData = data.filter((item) =>
        item[type]?.toLowerCase()?.includes(lowercasedValue)
      );
      setCurrentPageData(filteredData);
    } else {
      setCurrentPageData(data);
    }
  };

  const handleCategoryChange = (value) => {
    setFilterCategory(value);
    setSelectedCategory(value);
    setCurrentPageNo(1); // Reset to first page after filtering
  };

  const SkeletonRow = ({ columns }) => (
    <tr className="table-row">
      {columns.map((col, index) => (
        <td key={index} className="table-data" style={{ width: col.width }}>
          <div className="skeleton-box"></div>
        </td>
      ))}
    </tr>
  );

  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedStatus(null);
    setAlertDate(null);
    setScheduleDate(null);
    setFilterSchedule(null);
    setExpiryDate(null);
    setCurrentPageData(data);
    // filter resting
    setFilterTitle("");
    setFilterAuthor("");
    setFilterPublish(null);
    setFilterExpiry(null);
    setFilterCategory(null);
    setCurrentPageNo(1); // Reset to first page after clearing filters
  };

  const onDataClick = (row) => {
    dataClick(row);
  };

  useEffect(() => {
    clearFilters();
  }, [tabState]);

  const getColorByTagColor = (tagColor) => {
    const colorItem = colorData.find((item) => item.id === tagColor);
    return colorItem ? colorItem.value : "";
  };

  return (
    <div className="table-wrapper">
      <table className="table-comp">
        <thead className="table-head">
          <tr className="table-row">
            {column.map((col, i) => (
              <th
                className="table-header"
                key={i}
                style={{ minWidth: col.width }}
              >
                <span className="flex align-center gap-3">{col.title}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-body">
          <tr className="table-row">
            {column.map((col, i) => (
              <td className="table-data" style={{ width: col.width }} key={i}>
                {col.filter === "searchTitle" ? (
                  <SearchComp
                    className="table-search"
                    height={33}
                    bgNone
                    placeholder="Search titles..."
                    value={filterTitle}
                    onChange={(e) => handleSearchChange(e, "title")}
                  />
                ) : col.filter === "searchRecipients" ? (
                  <SearchComp
                    className="table-search"
                    height={33}
                    bgNone
                    placeholder="Search author..."
                    value={filterAuthor}
                    onChange={(e) => handleSearchChange(e, "author")}
                  />
                ) : col.filter === "selectCategory" ? (
                  <SelectComp
                    className="table-select"
                    value={selectedCategory}
                    placeholder={col.title}
                    onChange={handleCategoryChange}
                    data={col?.dropDown?.map((item) => ({
                      label: item?.name,
                      value: item?.value,
                    }))}
                  />
                ) : col?.filter === "datePickerAlert" ? (
                  <DatePickerComp
                    className="table-date-picker"
                    suffixIcon={<TableDatePicker />}
                    onChange={handleAlertDateChange}
                    setValue={setAlertDate}
                    value={alertDate}
                  />
                ) : col.filter === "datePickerExpiry" ? (
                  <DatePickerComp
                    className="table-date-picker"
                    suffixIcon={<TableDatePicker />}
                    onChange={handleExpiryDateChange}
                    setValue={setExpiryDate}
                    value={expiryDate}
                  />
                ) : col.filter === "datePickerScheduled" ? (
                  <DatePickerComp
                    className="table-date-picker"
                    suffixIcon={<TableDatePicker />}
                    onChange={handleScheduleDateChange}
                    setValue={setScheduleDate}
                    value={scheduleDate}
                  />
                ) : null}
              </td>
            ))}
          </tr>
          {(filterTitle ||
            selectedCategory ||
            selectedStatus ||
            alertDate ||
            filterAuthor ||
            filterSchedule ||
            expiryDate) && (
            <div className="clr-btn-container">
              <ButtonComp height={16} variant="link" onClick={clearFilters}>
                Clear filters
              </ButtonComp>
            </div>
          )}
          {!isLoading ? (
            currentPageData && currentPageData.length > 0 ? (
              currentPageData.map((row) => (
                <tr className="table-row" key={row?.id}>
                  {column?.map((col, colIndex) => (
                    <td
                      style={{ width: col?.width }}
                      className="table-data"
                      key={colIndex}
                    >
                      {colIndex === 0 ? (
                        <span className="flex relative align-center ">
                          <div className="ml-3">
                            {row?.banner_img?.length > 0 ? (
                              <AvatarComp
                                blue
                                size={32}
                                shape="square"
                                src={row.banner_img}
                              />
                            ) : (
                              <AvatarComp size={32} shape="square" blue>
                                {row[
                                  col?.title?.toLowerCase()?.replace(" ", "")
                                ]?.charAt(0)}
                              </AvatarComp>
                            )}
                          </div>
                          <TooltipComp
                            title={
                              row[col?.title?.toLowerCase()?.replace(" ", "")]
                            }
                          >
                            <span
                              className="table-title"
                              onClick={() => {
                                onDataClick(row);
                              }}
                            >
                              {row[col.title?.toLowerCase()?.replace(" ", "")]}
                              {row?.top_news && (
                                <span className="top_news">
                                  <TopNewsIcon />
                                </span>
                              )}
                            </span>
                          </TooltipComp>
                        </span>
                      ) : col?.filter === "searchRecipients" && row?.author ? (
                        <div className="flex">
                          {row?.author_image?.length > 0 ? (
                            <span className="mr-4">
                              <AvatarComp blue src={row?.author_image} />
                            </span>
                          ) : (
                            <span className="mr-4">
                              <AvatarComp blue>
                                {row?.author?.replace(/\s/g, "")[0]}
                              </AvatarComp>
                            </span>
                          )}
                          <span className="flex align-center gap-2 overflow-hidden">
                            {row?.author}
                          </span>
                        </div>
                      ) : col?.chip ? (
                        <div>
                          <TagComp color={getColorByTagColor(row.tag_color)}>
                            {row[col.title?.toLowerCase()]}
                          </TagComp>
                        </div>
                      ) : col.filter === "datePickerAlert" ||
                        col.filter === "datePickerExpiry" ? (
                        col.filter === "datePickerAlert" ? (
                          row["tabDate"]
                        ) : (
                          row[col.title?.toLowerCase().replace(" ", "")] ||
                          "N/A"
                        )
                      ) : (
                        col.filter === "datePickerScheduled" && (
                          <span className="flex align-center gap-2">
                            {row.scheduled_date}
                          </span>
                        )
                      )}
                      <div className="action-container">
                        {editAction &&
                          ((row?.allow_resubmission_flg && tabActive === "4") ||
                          (tabActive === "5" && isPermit(3, "edit")) ? (
                            <ButtonComp
                              onClick={() => handleEdit(row)}
                              height={32}
                              rounded
                            >
                              <EditIcon />
                            </ButtonComp>
                          ) : null)}

                        {isPermit(3, "delete") && (
                          <ButtonComp
                            onClick={() => onDeleteAction(row)}
                            height={32}
                            rounded
                          >
                            <DeleteIcon />
                          </ButtonComp>
                        )}
                        {copyAction && isPermit(3, "create") && (
                          <ButtonComp
                            onClick={() => handleDuplicate(row)}
                            height={32}
                            rounded
                          >
                            <CopyIcon />
                          </ButtonComp>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <EmptyState />
            )
          ) : (
            Array(10)
              .fill(0)
              .map((_, index) => <SkeletonRow key={index} columns={column} />)
          )}
        </tbody>
      </table>
      <PaginationComp
        currentPageNo={currentPageNo}
        total={totalPageNo}
        onChange={handlePageChange}
      />
    </div>
  );
}

export default NewsTable;
